import { BlurView } from 'expo-blur'
import React from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { Avatar } from 'react-native-paper'

import { DS } from '../../design-system'
import { color, fontFamily } from '../../theme'
import DotsIcon from './assets/Three-Dots.svg'
import { getContrastTextColor } from './getContrastColor'

import type { DailyInspoReactionType } from '../../models/session/session-daily'
const EMOJIS = ['👑', '👏', '🙌', '🔥', '💃🏻']

interface UserResponseCardProps {
  responseId: string
  userName: string
  response: string
  handleEdit: () => void
  setEditResponseDataId: (id: string) => void
  setEditResponseDataMsg: (msg: string) => void
}

interface OtherResponseCardProps {
  responseId: string
  userName: string
  response: string
  emojis: DailyInspoReactionType[]
  isEmojiListVisible: boolean
  setIsEmojiListVisible: (isEmojiListVisible: boolean) => void
  handleReaction: (emoji: string, responseId: string, response: string, author: string) => void
  onPressReport: (id: string, author: string, response: string) => void
  selectedIdForImojiSelection: string
  setSelectedIdForImojiSelection: (responseId: string) => void
  setReportResponseId: (reportResponseId: string) => void
  userBadgeColor: string
}

export const UserResponseCard = (props: UserResponseCardProps) => {
  return (
    <DS.Frame style={styles.userResponseCard}>
      <DS.Frame row gap="12">
        <Avatar.Text
          size={32}
          label={props.userName[0]?.toUpperCase() || 'U'}
          color={color.palette.black}
          style={{ backgroundColor: color.palette.pink }}
          labelStyle={{ fontFamily: fontFamily.GTAmericaExtended, fontSize: 12, color: color.palette.black }}
        />
        <DS.Frame style={{ flex: 1 }}>
          <DS.Frame style={styles.userNameView}>
            <DS.Text variant="Body Small/Medium">{'Your response'}</DS.Text>
          </DS.Frame>
          <DS.Text variant="Body Small/Regular" style={{ marginTop: 4 }}>
            {props.response}
          </DS.Text>
          <TouchableOpacity
            style={styles.editView}
            onPress={() => {
              props.handleEdit()
              props.setEditResponseDataId(props.responseId)
              props.setEditResponseDataMsg(props.response)
            }}
          >
            <DS.Text variant="Body Small/Medium" color="Global/Black">
              Edit
            </DS.Text>
          </TouchableOpacity>
        </DS.Frame>
      </DS.Frame>
    </DS.Frame>
  )
}

export const OtherResponseCard = (props: OtherResponseCardProps) => {
  return (
    <DS.Frame bg="Global/White" style={styles.otherResponseCard}>
      <DS.Frame row gap="12">
        <Avatar.Text
          size={32}
          label={props.userName[0]?.toUpperCase() || 'U'}
          color={color.palette.black}
          style={{ backgroundColor: props.userBadgeColor }}
          labelStyle={{
            fontFamily: fontFamily.GTAmericaExtended,
            fontSize: 12,
            color: getContrastTextColor(props.userBadgeColor)
          }}
        />
        <DS.Frame style={{ flex: 1 }}>
          <DS.Frame style={styles.userNameView}>
            <DS.Text variant="Body Small/Medium">{props.userName}</DS.Text>
            <DS.Frame>
              <TouchableOpacity
                style={{ paddingLeft: 5 }}
                onPress={() => {
                  props.onPressReport(props.responseId, props.userName, props.response)
                }}
              >
                <DotsIcon />
              </TouchableOpacity>
            </DS.Frame>
          </DS.Frame>
          <DS.Text variant="Body Small/Regular" style={{ marginTop: 4 }}>
            {props.response}
          </DS.Text>
          {props.isEmojiListVisible && props.responseId === props.selectedIdForImojiSelection && (
            <BlurView intensity={80} tint="light" style={styles.handRankings}>
              {EMOJIS.map((item, index) => {
                return (
                  <TouchableOpacity
                    key={`${item}-${index}`}
                    onPress={() => props.handleReaction(item, props.responseId, props.response, props.userName)}
                  >
                    <DS.Text variant="Heading 4/Medium">{item}</DS.Text>
                  </TouchableOpacity>
                )
              })}
            </BlurView>
          )}
          <View key="emojiListContainer" style={styles.emojiListContainer}>
            <TouchableOpacity
              onPress={() => {
                props.setIsEmojiListVisible(!props.isEmojiListVisible)
                props.setSelectedIdForImojiSelection(props.responseId)
              }}
            >
              <Image source={require('../home-screen/assets/emoji-icon.png')} style={styles.emojiIcon} />
            </TouchableOpacity>
            {props.emojis?.map((item) => {
              if (item.count)
                return (
                  <TouchableOpacity
                    onPress={() => props.handleReaction(item.emoji, props.responseId, props.response, props.userName)}
                    key={`${item}`}
                    style={{
                      paddingVertical: 5,
                      justifyContent: 'center',
                      alignItems: 'center',
                      alignSelf: 'center'
                    }}
                  >
                    <DS.Text variant="Body Small/Regular">{`${item.emoji} ${
                      item.count > 1 ? item.count : '\b'
                    }`}</DS.Text>
                  </TouchableOpacity>
                )
            })}
          </View>
        </DS.Frame>
      </DS.Frame>
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F6F2E9',
    paddingHorizontal: 24
  },
  otherResponseCard: {
    marginBottom: 4,
    borderRadius: 12,
    backgroundColor: DS.palette['Global/White'],
    padding: DS.spacing[16],
    gap: 12
  },
  userResponseCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    marginBottom: 4,
    padding: DS.spacing[16],
    borderRadius: 12,
    gap: 12
  },
  userNameView: {
    justifyContent: 'space-between',
    height: 20,
    flexDirection: 'row'
  },
  emojiListContainer: {
    overflow: 'visible',
    flexDirection: 'row',
    width: '100%',
    marginLeft: 2,
    marginTop: 4,
    gap: 12
  },
  emojiIcon: {
    width: 32,
    height: 32
  },
  reactedEmojiContainer: {
    backgroundColor: DS.palette['Global/White'],
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4
  },
  handRankings: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    gap: 8,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    width: '80%',
    alignSelf: 'center',
    position: 'absolute',
    bottom: 25,
    zIndex: 2,
    overflow: 'hidden'
  },
  editView: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    alignItems: 'center',
    borderRadius: 48,
    marginTop: 8,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    gap: 4
  }
})
